import React, {useEffect, useMemo, useState} from 'react';

import {Button, Input, Select} from '@/components';
import Modal from '@/components/Modals';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import {logRejection} from '@/utils/logRejection';

type TargetOrg = {id: string; name: string; email?: string};

type MoveMemberModalProps = {
  isOpen: boolean;
  onClose: () => void;
  members: AfriexBusinessUser[];
  onConfirm: (
    userId: string,
    targetBusinessId: string,
  ) => Promise<boolean | void>;
  onSearchBusinesses: (term: string) => Promise<TargetOrg[]>;
  isLoading?: boolean;
};

// Duplicate consolidation: request that a member of this organization join a
// confirmed target organization instead. This does NOT move anyone directly —
// it sends the target org's owner an approval request, exactly like any other
// join request. The owner assigns a role on approval, and the member must set
// a new password to accept; only then does their membership here retire.
// Compliance uses this after confirming the person genuinely belongs to the
// target org.
const MoveMemberModal = ({
  isOpen,
  onClose,
  members,
  onConfirm,
  onSearchBusinesses,
  isLoading,
}: MoveMemberModalProps) => {
  const [userId, setUserId] = useState('');
  const [orgQuery, setOrgQuery] = useState('');
  const [orgResults, setOrgResults] = useState<TargetOrg[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<TargetOrg | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const memberOptions = useMemo(
    () =>
      members.map((member) => ({
        value: member.id,
        key: member.email
          ? `${member.name?.fullName} (${member.email})`
          : (member.name?.fullName ?? member.id),
      })),
    [members],
  );

  const reset = () => {
    setUserId('');
    setOrgQuery('');
    setOrgResults([]);
    setSelectedOrg(null);
  };

  // Debounced target-org search. Skips searching once an org is selected (the
  // input then shows the chosen org's name).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (selectedOrg || !orgQuery.trim()) {
      setOrgResults([]);
      return;
    }
    let active = true;
    setIsSearching(true);
    // `setTimeout` expects a `=> void` callback, so the search runs as a
    // then/catch chain rather than an async callback whose rejection would
    // escape. The wired implementation (useBusinessDetails.searchTargetBusinesses)
    // toasts its own failures and resolves to [], so this handler only fires
    // for an implementation that actually rejects.
    const handle = setTimeout(() => {
      onSearchBusinesses(orgQuery)
        .then((results) => {
          if (active) {
            setOrgResults(results);
            setIsSearching(false);
          }
        })
        .catch(logRejection('Failed to search target organizations'));
    }, 350);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [orgQuery, selectedOrg, isOpen, onSearchBusinesses]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (!userId || !selectedOrg) {
      return;
    }
    const result = await onConfirm(userId, selectedOrg.id);
    // Keep the modal open on failure (error already surfaced via toast).
    if (result !== false) {
      reset();
      onClose();
    }
  };

  const canConfirm = !!userId && !!selectedOrg && !isLoading;

  const renderOrgPicker = () => {
    if (selectedOrg) {
      return (
        <p className="mt-1 text-xs text-cyan-700 dark:text-cyan-300">
          Selected: {selectedOrg.name}
          {selectedOrg.email ? ` (${selectedOrg.email})` : ''}{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              setSelectedOrg(null);
              setOrgQuery('');
            }}
          >
            change
          </button>
        </p>
      );
    }

    if (isSearching) {
      return <p className="mt-1 text-xs text-gray-500">Searching…</p>;
    }

    return (
      orgResults.length > 0 && (
        <ul className="mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
          {orgResults.map((org) => (
            <li key={org.id}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 dark:hover:bg-cyan-950 text-gray-800 dark:text-gray-100"
                onClick={() => {
                  setSelectedOrg(org);
                  setOrgResults([]);
                }}
              >
                <span className="block font-medium">{org.name}</span>
                {org.email && (
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {org.email}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="w-10/12 sm:w-6/12 md:w-5/12 lg:w-4/12 xl:w-3/12 "
      title="Move member"
      closeOnOutsideClick
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            colorScheme="cyan"
            disabled={!canConfirm}
            isLoading={!!isLoading}
            loadingText="Sending…"
            onClick={handleConfirm}
          >
            Move member
          </Button>
        </div>
      }
    >
      <div className="pt-3 px-2 pb-5">
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Request that a member of this organization join another organization
          instead. This sends the target organization&apos;s owner an approval
          request — it does not move anyone directly. The owner assigns a role
          and the member must set a new password to accept; only then does their
          membership here retire. Use this only after confirming the person
          belongs to the target organization.
        </p>

        <div className="flex flex-col gap-4">
          <Select
            label="Member to move"
            placeholder={
              memberOptions.length ? 'Select a member' : 'No members'
            }
            value={userId}
            options={memberOptions}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setUserId(e.target.value)
            }
          />

          <div>
            <Input
              label="Target organization"
              placeholder="Search by organization name"
              value={selectedOrg ? selectedOrg.name : orgQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSelectedOrg(null);
                setOrgQuery(e.target.value);
              }}
              className="w-full isFull"
            />
            {renderOrgPicker()}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MoveMemberModal;
